import {MediaType, UploadProgressCallbackType, UploadProgressType, UploadQueueType} from "./UploaderTypes";
import UploadSegment from "./UploadSegment";
import API from "./API";
import Logger from "./Logger";
import CryptoJS from "crypto-js";

export default class Upload {
  static maxConcurrentSegments = 1;
  private pendingSegments: Array<UploadSegment> = [];
  private file: File;
  private id?: number;
  private mediaId: number;
  private seasonId?: number;
  private showId?: number;
  private type: MediaType;
  private fileSize?: number;
  private completedSize = 0;
  private cancelled = false;

  private progressCallback: UploadProgressCallbackType;


  constructor(type: MediaType, file: File, progressCallback: UploadProgressCallbackType, mediaId: number, seasonId?: number, showId?: number) {
    this.type = type;
    this.file = file;
    this.mediaId = mediaId;
    this.seasonId = seasonId;
    this.showId = showId;
    this.progressCallback = progressCallback;
  }

  getFile(): File {
    return this.file;
  }

  setFile(file: File) {
    this.file = file;
  }

  getId(): number | undefined {
    return this.id;
  }

  /**
   * Generate a checksum associated with the file loaded
   * @private
   */
  private async generateChecksum()
  {
    let md5 = CryptoJS.algo.MD5.create();

    for (let i = 0; i < this.file.size; i += UploadSegment.maxSegmentSize) {
      // Use segment size as a base level of acceptable chunk size - relatively arbitrary though
      const segmentData = await this.fetchSegment(i);

      md5 = md5.update(CryptoJS.lib.WordArray.create(segmentData as any));
    }

    return md5.finalize().toString();
  }

  /**
   * Fetch a segment of data from the file loaded - helper for generateChecksum
   * @param offset byte offset
   * @private
   */
  private fetchSegment(offset: number): Promise<string | ArrayBuffer | null > {
    return new Promise<string | ArrayBuffer | null>((resolve, reject) => {
      const reader = new FileReader();
      const segment = this.file.slice(offset, offset + UploadSegment.maxSegmentSize);

      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsArrayBuffer(segment);
    });

  }

  async ready(token: () => string): Promise<number> {
    this.onUploadProgress(0);

    this.fileSize = this.file.size;
    const segmentCount = Math.ceil(this.fileSize / UploadSegment.maxSegmentSize);

    let data = {
      part_count: segmentCount,
      type: this.type,
      media_id: this.mediaId,
      checksum: await this.generateChecksum()
    };

    if (this.type === MediaType.Episode) {
      data = {
        ...{
          season_id: this.seasonId,
          show_id: this.showId,
        },
        ...data
      };
    }

    const api = new API(token());

    const response = await api.uploadRequest().store(data);

    this.id = response.data.id;
    for (const part of response.data.parts) {
      const offset = (part.part_number - 1) * UploadSegment.maxSegmentSize;

      const size = Math.min(this.fileSize - offset, UploadSegment.maxSegmentSize);
      const segment = new UploadSegment(
        part.part_number,
        offset,
        size,
        part.is_complete,
        this
      );
      this.pendingSegments.push(segment);
    }


    return this.id!;
  }

  async upload(token: () => string) {

    while (this.pendingSegments.length > 0) {
      if (this.cancelled) {
        break;
      }
      const segment = this.pendingSegments.pop()!;

      try {
        await segment.upload(token);
      } catch (error) {
        this.pendingSegments = [];
        throw error;
      }
    }

    if (!this.cancelled) {
      const api = new API(token());
      await api.completeUploadRequest(this.id!);
    }
  }

  public addCompletedBytes(completed: number) {
    this.completedSize += completed;
    Logger.log("completed - " + this.completedSize);
    Logger.log("total - " + this.fileSize);
    this.onUploadProgress(this.completedSize * 100 / (this.fileSize || 0));
  }

  cancel() {
    this.cancelled = true;
  }

  public onUploadProgress(progress: number) {
    const progressState = {type: UploadProgressType.Upload, progress: progress};
    this.progressCallback(this.asData(), progressState);
  }

  private asData(): UploadQueueType {
    return this.type === MediaType.Episode ? {
        mediaId: this.mediaId,
        mediaType: this.type,
        seasonId: this.seasonId!,
        showId: this.showId!
      }
      : {
        mediaId: this.mediaId,
        mediaType: this.type
      };
  }

}
